import type { RouteLocationRaw } from 'vue-router'

import { defineStore } from 'pinia'

import type { ResolvedMainSidebarGroup } from '@/types/internal/mainSidebarTypes'
import type { RedirectRoute } from '@/utilities/routers.ts'

import MainSidebarMap from '@/constants/mainSidebarMap'
import { useSideNavStore } from '@/stores/sideNavStore.ts'
import { buildRouteLocation } from '@/utilities/routeLocation'
import { resolveMenuLink } from '@/utilities/sidebar/sidebarHelpers'

export const useSidebarStore = defineStore('sidebar', {
  state: () => ({}),

  actions: {
    async initializeSidebarContext(): Promise<void> {
      const sideNavStore = useSideNavStore()
      await sideNavStore.initialize()
    },
  },

  getters: {
    /**
     * Returns the sidebar structure based on permissions
     */
    filteredMainSidebarGroup(): ResolvedMainSidebarGroup[][] {
      return MainSidebarMap.map((sectionObj) => {
        return Object.values(sectionObj)
          .map((group) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const menuItems = (group.menuItems ?? [])
              .map((mi) => {
                if (mi.visible && !mi.visible()) return null

                const routeLink = mi.link()
                const resolvedRoute = resolveMenuLink(routeLink, mi.subRoutes)
                if (!resolvedRoute) return null

                const resolvedLink = buildRouteLocation(resolvedRoute, mi.params?.())
                if (!resolvedLink) return null

                return {
                  ...(mi.beta ? { beta: mi.beta } : {}),
                  link: resolvedLink,
                  route: routeLink,
                  text: mi.text,
                  ...(mi.subRoutes ? { subRoutes: mi.subRoutes } : {}),
                }
              })
              .filter((mi): mi is NonNullable<typeof mi> => mi !== null)

            // Resolve the optional group header link through the same permission
            // and location handling as menu items. Falls back to undefined when the
            // target is inaccessible, so the header defaults to the first menu item.
            const {
              groupLink: groupLinkResolver,
              settingsLink: settingsLinkResolver,
              ...groupRest
            } = group
            let groupLink: RouteLocationRaw | undefined = undefined
            if (groupLinkResolver) {
              const groupRouteLink = groupLinkResolver()
              const resolvedGroupRoute = resolveMenuLink(groupRouteLink)
              const resolvedGroupLink = resolvedGroupRoute
                ? buildRouteLocation(resolvedGroupRoute)
                : null
              if (resolvedGroupLink) groupLink = resolvedGroupLink
            }

            // Resolve the optional per-module settings link (gear icon) the same way.
            let settingsLink: RouteLocationRaw | undefined = undefined
            if (settingsLinkResolver) {
              const settingsRouteLink = settingsLinkResolver()
              const resolvedSettingsRoute = resolveMenuLink(settingsRouteLink)
              const resolvedSettingsLink = resolvedSettingsRoute
                ? buildRouteLocation(resolvedSettingsRoute)
                : null
              if (resolvedSettingsLink) settingsLink = resolvedSettingsLink
            }

            return {
              ...groupRest,
              ...(groupLink ? { groupLink } : {}),
              menuItems,
              ...(settingsLink ? { settingsLink } : {}),
            }
          })
          .filter((group) => group.menuItems.length > 0)
      }).filter((groups) => groups.length > 0)
    },

    /**
     * Returns the first available menu link (for redirects etc.)
     */
    getFirstMenuLink(): false | RedirectRoute {
      const groups = this.filteredMainSidebarGroup
      const firstLink = groups[0]?.[0]?.menuItems?.[0]?.link

      if (!firstLink) return false
      if (typeof firstLink === 'string') return { path: firstLink }

      if ('name' in firstLink && typeof firstLink.name === 'string') {
        return firstLink.params
          ? { name: firstLink.name, params: firstLink.params }
          : { name: firstLink.name }
      }

      if ('path' in firstLink && firstLink.path && firstLink.path !== '') {
        return { path: firstLink.path }
      }

      return false
    },
  },
})
